/**
 * FE-17: System Tab -- Summary success/fail, storage growth, webhook failures.
 * DatePresets + KPI cards (alert coloring) + success rate TrendLineChart + collapsible table.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchSystemMetrics } from "../../api/admin-metrics";
import type { SystemMetrics } from "../../api/admin-metrics";
import { Td, Th, tableStyles } from "../../components/DataTable";
import { DatePresets } from "../../components/DatePresets";
import { TrendLineChart } from "../../components/charts/TrendLineChart";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { fmt, fmtBytes, pct } from "./helpers";

function defaultRange(): { start_date: string; end_date: string } {
  const end = new Date().toISOString().slice(0, 10);
  const start = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  return { start_date: start, end_date: end };
}

type ChartRow = { readonly date: string; readonly success_rate: number };

export function SystemTab(): React.JSX.Element {
  const [range, setRange] = useState(defaultRange);
  const { data, error, loading, load } = useSystemData();
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    void load(range.start_date, range.end_date);
  }, [load, range]);

  const chartData: ReadonlyArray<ChartRow> = useMemo(() => {
    if (!data) return [];
    return data.rows.map((row) => ({
      date: row.date,
      success_rate:
        row.summary_requests > 0
          ? row.summary_success / row.summary_requests
          : 0,
    }));
  }, [data]);

  const chartLines = [
    {
      key: "success_rate",
      color: "#16a34a",
      label: t("system_tab.success_rate"),
    },
  ] as const;

  return (
    <div>
      <DatePresets value={range} onChange={setRange} />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && !data && (
        <p style={shared.loadingText}>{t("common.loading")}</p>
      )}
      {data && <KPICards data={data} />}
      {data && (
        <section style={shared.section}>
          <TrendLineChart data={chartData} lines={chartLines} xKey="date" />
        </section>
      )}
      <section style={shared.section}>
        <button
          type="button"
          onClick={() => setShowTable(!showTable)}
          style={s.toggleBtn}
        >
          {showTable ? t("common.hide_details") : t("common.show_details")}
        </button>
        {showTable && data && <DailyTable rows={data.rows} />}
      </section>
    </div>
  );
}

// --- KPI Cards ---

function KPICards({ data }: { data: SystemMetrics }): React.JSX.Element {
  const { totals } = data;
  const isLow = totals.success_rate < 0.95;
  return (
    <section style={shared.section}>
      <div style={shared.cardGrid}>
        <Card
          label={t("system_tab.summary_requests")}
          value={fmt(totals.summary_requests)}
        />
        <Card
          label={t("system_tab.success_rate")}
          value={pct(totals.success_rate)}
          alert={isLow}
        />
        <Card
          label={t("system_tab.storage")}
          value={fmtBytes(totals.storage_bytes)}
        />
        <Card
          label={t("system_tab.webhook_failures")}
          value={fmt(totals.webhook_failures)}
          alert={totals.webhook_failures > 0}
        />
      </div>
    </section>
  );
}

function Card({
  label,
  value,
  alert,
}: {
  label: string;
  value: string;
  alert?: boolean;
}): React.JSX.Element {
  return (
    <div style={{ ...shared.card, padding: 20 }}>
      <span style={s.cardLabel}>{label}</span>
      <span
        style={{
          ...shared.bigNumber,
          color: alert ? "#dc2626" : "var(--color-text)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// --- Daily Table ---

function DailyTable({
  rows,
}: {
  rows: ReadonlyArray<SystemMetrics["rows"][number]>;
}): React.JSX.Element {
  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={shared.sectionTitle}>{t("system_tab.daily_breakdown")}</h3>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <Th>{t("system_tab.col_date")}</Th>
            <Th>{t("system_tab.col_summary_req")}</Th>
            <Th>{t("system_tab.col_success")}</Th>
            <Th>{t("system_tab.col_failed")}</Th>
            <Th>{t("system_tab.col_storage")}</Th>
            <Th>{t("system_tab.col_wh_failures")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.date}>
              <Td>{row.date}</Td>
              <Td mono>{fmt(row.summary_requests)}</Td>
              <Td mono>{fmt(row.summary_success)}</Td>
              <Td mono>{fmt(row.summary_failed)}</Td>
              <Td mono>{fmtBytes(row.storage_bytes)}</Td>
              <Td mono>{fmt(row.webhook_failures)}</Td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <Td colSpan={6}>{t("common.no_data_for_period")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// --- Hooks ---

function useSystemData(): {
  data: SystemMetrics | null;
  error: string | null;
  loading: boolean;
  load: (start: string, end: string) => Promise<void>;
} {
  const [data, setData] = useState<SystemMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (start: string, end: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        setData(await fetchSystemMetrics({ start_date: start, end_date: end }));
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : t("common.failed_to_load"),
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, error, loading, load };
}

// --- Styles ---

const s: Record<string, React.CSSProperties> = {
  cardLabel: {
    display: "block",
    fontSize: 13,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    marginBottom: 4,
  },
  toggleBtn: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    cursor: "pointer",
  },
};
